import React, { useCallback, useState } from 'react'
import Backdrop from '@material-ui/core/Backdrop'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Drawer from '@material-ui/core/Drawer'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/styles/makeStyles'
import {
  InstantSearch,
  connectHits,
  connectStats,
  Configure,
} from 'react-instantsearch-dom'
import { ReactComponent as SliderIcon } from 'images/event-search/slider.svg'
import Filters from './filters'
import EventCard from './event-card'
import useSearchConfiguration from './use-search-configuration'
import useSearchState from './use-search-state'
import { JRL_EVENT_SEARCH_INDEX } from 'env'
import {
  EmptySelection,
  NoResults,
  SearchBox,
  AlgoliaPagination,
  client as searchClient,
} from 'components/search'

const drawerContainer = window && (() => window.document.body)

const useStyles = makeStyles(theme => ({
  filterBox: {
    backgroundColor: 'white',
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(4),
    position: 'relative',
    zIndex: 1,

    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },

  containerBox: {
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(10.25),
  },

  resultsArea: {
    backgroundColor: '#FAFAFA',
    marginTop: theme.spacing(3),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),

    [theme.breakpoints.up('md')]: {
      paddingRight: theme.spacing(4),
      paddingLeft: theme.spacing(4),
    },
  },

  searchTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
  },

  resultCount: {
    fontSize: '16px',
    color: '#2C2D36',
  },

  filterButton: {
    padding: '14px',
  },
}))

const ListHits = ({ hits }) => (
  <div>
    {hits.map(hit => (
      <EventCard key={hit.objectID} hit={hit} />
    ))}

    {hits.length === 0 && (
      <NoResults surveyName="jr-league-programs-interest" />
    )}
  </div>
)

const ProgramResults = connectHits(ListHits)

const ResultCountDisplay = ({ nbHits }) => {
  const classes = useStyles()

  return (
    <Typography variant="body1" className={classes.resultCount}>
      {nbHits} Result{nbHits !== 1 ? 's' : ''}
    </Typography>
  )
}

const ResultCount = connectStats(ResultCountDisplay)

export default function EventSearch() {
  const [searchState, setSearchState] = useSearchState()
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [showBackdrop, setShowBackdrop] = useState(false)
  const configuration = useSearchConfiguration(searchState)
  const classes = useStyles()

  const { selection } = searchState

  const onDrawerToggle = () => {
    setIsFilterDrawerOpen(!isFilterDrawerOpen)
  }

  const setSelection = useCallback(
    selection => setSearchState({ selection, ...selection.initialFilters }),
    [setSearchState],
  )

  const clearSelection = useCallback(() => setSelection(new EmptySelection()), [
    setSelection,
  ])

  const onSelect = useCallback(
    selection => {
      if (selection) {
        setSelection(selection)
      } else {
        clearSelection()
      }
    },
    [clearSelection, setSelection],
  )

  const onAutocompleteOptions = ({ isOpen }) => setShowBackdrop(isOpen)

  const filters = (
    <Filters searchState={searchState} setSearchState={setSearchState} />
  )

  const searchBox = (
    <Grid item md={4} sm={10} xs={10}>
      <SearchBox
        value={selection}
        onChange={onSelect}
        onAutocompleteOptions={onAutocompleteOptions}
        searchIndex={JRL_EVENT_SEARCH_INDEX}
      />
    </Grid>
  )

  return (
    <Box className={classes.containerBox}>
      <Backdrop open={showBackdrop} style={{ zIndex: 1 }} />

      <InstantSearch
        searchClient={searchClient}
        indexName={JRL_EVENT_SEARCH_INDEX}
      >
        <Configure {...configuration} />
        <div className={classes.filterBox}>
          <Hidden smDown>
            <Grid container spacing={3}>
              {searchBox}
              {filters}
            </Grid>
          </Hidden>

          <Hidden mdUp>
            <Box pr={5}>
              <Grid container spacing={3}>
                {searchBox}

                <Grid item xs={2}>
                  <Button
                    variant="outlined"
                    className={classes.filterButton}
                    onClick={onDrawerToggle}
                  >
                    <SliderIcon />
                  </Button>
                </Grid>
                <Drawer
                  anchor="right"
                  variant="temporary"
                  container={drawerContainer}
                  width="100%"
                  open={isFilterDrawerOpen}
                  onClose={onDrawerToggle}
                >
                  <Box p={4}>
                    <Grid container spacing={3}>
                      {filters}
                      <Grid item xs="12">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={onDrawerToggle}
                        >
                          Search
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Drawer>
              </Grid>
            </Box>
          </Hidden>
        </div>

        <div className={classes.resultsArea}>
          <Grid container spacing={4} direction="column">
            <Grid item>
              <Typography variant="h4" className={classes.searchTitle}>
                Programs near {selection.nearLabel}
              </Typography>

              <Typography
                variant="b1"
                style={{ color: '#FF7500', fontSize: '12px' }}
              >
                Please note the mileage distances you see on this page are
                approximate and may not reflect the actual driving mileage from
                your current location to the facility.
              </Typography>

              <ResultCount />
            </Grid>

            <Grid item>
              <ProgramResults />
            </Grid>

            <Grid container item justify="center">
              <AlgoliaPagination />
            </Grid>
          </Grid>
        </div>
      </InstantSearch>
    </Box>
  )
}
